import { FormikProps } from 'formik';
import { isEqual, debounce, forEach } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../../api/useApiRequest';
import { parseNumberOrNull } from '../../../helpers/stringHelpers';
import { Form, FormHeaderFieldsContainer, TopLevelThermostatContainer } from '../../../shared/form/Form';
import { CheckboxField } from '../../../shared/form/inputs/CheckboxField';
import { SelectField } from '../../../shared/form/inputs/SelectField';
import { TextAreaField } from '../../../shared/form/inputs/TextAreaField';
import { TextInputField } from '../../../shared/form/inputs/TextInputField';
import { Project } from '../../projects/project-section/ProjectHeader';
import { AnimateInContextProvider } from '../../shared/quote-or-design-form/AnimateInContext';
import { DataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import {
  QuoteOrDesignFormContext,
  QuoteOrDesignFormContextValue,
} from '../../shared/quote-or-design-form/QuoteOrDesignFormContext';
import {
  nonSharedThermostatSelectOptions,
  ThermostatTypeCode,
} from '../../shared/quote-or-design-form/quoteOrDesignFormData';
import { roomFieldName } from '../../shared/quote-or-design-form/quoteOrDesignFormFieldNameHelpers';
import { UnitsForm } from '../../shared/quote-or-design-form/units/UnitsForm';
import {
  DesignMaterialsCalculationsInputs,
  getDesignMaterialsCalculationsInputs,
} from './calculations/materials/designMaterialsCalculationsInputs';
import { DesignMaterialsCalculationsResponse } from './calculations/materials/designMaterialsCalculationsResponse';
import { DesignFormButtons, NextStepAfterSavingDesign } from './DesignFormButtons';
import { DesignFormModel, designHasHeatPump, designHasSolar, designHasUnderfloorHeating } from './designFormModel';
import { getDesignCalculationsInputs, DesignCalculationsInputs } from './calculations/designCalculationsInputs';
import { DesignCalculationsResponse } from './calculations/designCalculationsResponse';
import { FormFieldRow } from '../../../shared/form/FormFieldRow';
import { RevisionSelectField } from '../../shared/quote-or-design-form/RevisionSelectField';
import { spacing17 } from '../../../styling/spacing';
import { DesignFormWarningModal } from './DesignFormWarningModal';

type Props = FormikProps<DesignFormModel> & {
  project: Project;
  dataForQuoteOrDesignForm: DataForQuoteOrDesignForm;
  disabled?: boolean;
  setNextStepAfterSave: (nextStep: NextStepAfterSavingDesign) => void;
};

type DesignCalculationsCommand = {
  inputs: DesignCalculationsInputs;
};

type DesignMaterialsCalculationsCommand = {
  inputs: DesignMaterialsCalculationsInputs;
};

export const DesignForm = ({
  project,
  dataForQuoteOrDesignForm,
  disabled,
  setNextStepAfterSave,
  ...formikProps
}: Props) => {
  const designCalculationsRequest = useApiRequest<DesignCalculationsResponse, DesignCalculationsCommand>({
    httpMethod: 'post',
    endpointUrl: 'design-calculations/calculate',
  });

  const designCalculationsInputs = useRef<DesignCalculationsInputs | null>(null);

  const debouncedRecalculateHeatLoss = useRef(
    debounce((formModel: DesignFormModel) => {
      const inputs = getDesignCalculationsInputs(formModel);

      if (isEqual(inputs, designCalculationsInputs.current)) {
        return;
      }

      designCalculationsInputs.current = inputs;

      if (inputs.units.length > 0) {
        void designCalculationsRequest.sendRequest({ requestBody: { inputs } });
      } else {
        designCalculationsRequest.reset();
      }
    }, 1000 /* 1 second */),
  );

  useEffect(() => {
    if (debouncedRecalculateHeatLoss.current != null) {
      debouncedRecalculateHeatLoss.current(formikProps.values);
    }
  }, [formikProps.values]);

  const designMaterialsCalculationsRequest = useApiRequest<
    DesignMaterialsCalculationsResponse,
    DesignMaterialsCalculationsCommand
  >({
    httpMethod: 'post',
    endpointUrl: 'design-materials-calculations/calculate',
  });

  const designMaterialsCalculationsInputs = useRef<DesignMaterialsCalculationsInputs | null>(null);

  const debouncedRecalculateMaterials = useRef(
    debounce((formModel: DesignFormModel) => {
      const inputs = getDesignMaterialsCalculationsInputs(formModel);
      if (isEqual(inputs, designMaterialsCalculationsInputs.current)) {
        return;
      }

      designMaterialsCalculationsInputs.current = inputs;

      if (inputs.rooms.length > 0) {
        void designMaterialsCalculationsRequest.sendRequest({ requestBody: { inputs } });
      } else {
        designMaterialsCalculationsRequest.reset();
      }
    }, 1000 /* 1 second */),
  );

  useEffect(() => {
    if (debouncedRecalculateMaterials.current != null) {
      debouncedRecalculateMaterials.current(formikProps.values);
    }
  }, [formikProps.values]);

  const designFormContextValue: QuoteOrDesignFormContextValue = {
    formType: 'Design',
    buildTypeCode: project.buildTypeCode,
    externalAirTemperatureInDegreesCelsius: project.externalAirTemperatureInDegreesCelsius,
    referenceData: dataForQuoteOrDesignForm,
    formProps: formikProps,
  };

  const design = formikProps.values;
  const hasUnderfloorHeating = designHasUnderfloorHeating(design);
  const hasHeatPump = designHasHeatPump(design);
  const hasSolar = designHasSolar(design);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warnings, setWarnings] = useState<Array<string>>([]);

  const rooms = useMemo(() => design.units.flatMap(({ manifolds }) => manifolds).flatMap(({ rooms }) => rooms ?? []), [
    design.units,
  ]);
  useEffect(() => {
    setWarnings([]);

    for (const room of rooms) {
      const isFloorCoveringSensitive =
        room.heatLossDetails?.floorCoveringName &&
        dataForQuoteOrDesignForm.floorCoveringsByName[room.heatLossDetails?.floorCoveringName].isSensitive;

      if (isFloorCoveringSensitive && parseNumberOrNull(room.numberOfFloorSensors) === 0) {
        setWarnings(existingWarnings => [
          ...existingWarnings,
          `Insufficient number of floor sensors for sensitive floor covering in ${room.name}`,
        ]);
      }
      if (isFloorCoveringSensitive && room.thermostatTypeCode === 'Shared') {
        setWarnings(existingWarnings => [
          ...existingWarnings,
          `You have selected a shared thermostat in ${room.name} which has a sensitive floor covering`,
        ]);
      }
    }
  }, [dataForQuoteOrDesignForm, rooms]);

  useEffect(() => {
    if (hasUnderfloorHeating && design.underfloorHeatingSalesOrderId == null) {
      formikProps.setFieldValue(
        'underfloorHeatingSalesOrderId',
        dataForQuoteOrDesignForm.underfloorHeatingSalesOrderOptions[0] || null,
      );
    } else if (!hasUnderfloorHeating && design.underfloorHeatingSalesOrderId != null) {
      formikProps.setFieldValue('underfloorHeatingSalesOrderId', null);
    }
  }, [hasUnderfloorHeating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasHeatPump && design.heatPumpSalesOrderId == null) {
      formikProps.setFieldValue('heatPumpSalesOrderId', dataForQuoteOrDesignForm.heatPumpSalesOrderOptions[0] || null);
    } else if (!hasHeatPump && design.heatPumpSalesOrderId != null) {
      formikProps.setFieldValue('heatPumpSalesOrderId', null);
    }
  }, [hasHeatPump]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasSolar && design.solarSalesOrderId == null) {
      formikProps.setFieldValue('solarSalesOrderId', dataForQuoteOrDesignForm.solarSalesOrderOptions[0] || null);
    } else if (!hasSolar && design.solarSalesOrderId != null) {
      formikProps.setFieldValue('solarSalesOrderId', null);
    }
  }, [hasSolar]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDefaultThermostatChange = (newDefaultThermostat: ThermostatTypeCode) => {
    forEach(design.units, (unit, unitIndex) => {
      forEach(unit.manifolds, (manifold, manifoldIndex) => {
        forEach(manifold.rooms, (room, roomIndex) => {
          if (room.thermostatTypeCode === design.defaultThermostatTypeCode) {
            formikProps.setFieldValue(
              roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatTypeCode'),
              newDefaultThermostat,
            );
          }
        });
      });
    });
  };

  return (
    <QuoteOrDesignFormContext.Provider value={designFormContextValue}>
      <Form disabled={disabled}>
        <FormHeader>
          <DesignFormFieldsContainer>
            <StyledFormFieldRow>
              <TextInputField name="title" label="Design Title" />
              <RevisionSelectField name="revisionCode" label="Revision" />
              <TextInputField name="revisionReason" label="Reason" />
              <TextInputField name="qrNumber" label="QR Number" prefix="QR" />
            </StyledFormFieldRow>
            <TextAreaField name="address" label="Address" />
            {hasUnderfloorHeating && (
              <SelectField
                name="underfloorHeatingSalesOrderId"
                label="Underfloor Heating Sales Order"
                options={dataForQuoteOrDesignForm.underfloorHeatingSalesOrderOptions}
              />
            )}
            {hasHeatPump && (
              <SelectField
                name="heatPumpSalesOrderId"
                label="Heat Pump Sales Order"
                options={dataForQuoteOrDesignForm.heatPumpSalesOrderOptions}
              />
            )}
            {hasSolar && (
              <SelectField
                name="solarSalesOrderId"
                label="Solar Sales Order"
                options={dataForQuoteOrDesignForm.solarSalesOrderOptions}
              />
            )}
            <TopLevelThermostatContainer>
              <SelectField
                name="defaultThermostatTypeCode"
                label="Default Thermostat"
                options={nonSharedThermostatSelectOptions}
                onChange={handleDefaultThermostatChange}
              />
              <CheckboxField name="includeNeoHub" label="Include neoHub" />
            </TopLevelThermostatContainer>
          </DesignFormFieldsContainer>
          <DesignFormButtons
            disabled={disabled}
            warnings={warnings}
            setIsWarningModalOpen={setIsWarningModalOpen}
            setNextStepAfterSave={setNextStepAfterSave}
            align="right"
            {...formikProps}
          />
        </FormHeader>

        <AnimateInContextProvider>
          <UnitsForm
            units={formikProps.values.units}
            designCalculationsRequest={{ ...designCalculationsRequest }}
            designMaterialsCalculationsRequest={{ ...designMaterialsCalculationsRequest }}
          />
        </AnimateInContextProvider>

        <DesignFormButtons
          disabled={disabled}
          warnings={warnings}
          setIsWarningModalOpen={setIsWarningModalOpen}
          setNextStepAfterSave={setNextStepAfterSave}
          {...formikProps}
          align="left"
        />

        <DesignFormWarningModal
          warnings={warnings}
          isOpen={isWarningModalOpen}
          closeModal={() => setIsWarningModalOpen(false)}
          {...formikProps}
        />
      </Form>
    </QuoteOrDesignFormContext.Provider>
  );
};

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DesignFormFieldsContainer = styled(FormHeaderFieldsContainer)`
  flex-grow: 1;
  max-width: ${spacing17};
`;

const StyledFormFieldRow = styled(FormFieldRow)`
  grid-template-columns: 2fr 1fr 2fr 1fr;
`;
